import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import {
  Accordion,
  Checkbox,
  Container,
  Divider,
  Form,
  Header,
  TextArea, 
  Message,
  Dropdown,
  Label,
  Icon,
  Button,
} from "semantic-ui-react";
import { parseInput, bufferToPrettyHex, detectInputFormat } from "./hexUtils";
import "./App.css";
import ProtobufDisplay from "./ProtobufDisplay";
import SmartDecoder from "./SmartDecoder";
import SchemaBasedDisplay from "./SchemaBasedDisplay";
import { decodeProto } from "./protobufDecoder";
import { ProtobufSchemaParser } from "./protobufSchemaParser";
import { useI18n } from "./i18n";

const LOCAL_STORAGE_KEY = "protobuf-decoder-state";
const STORAGE_VERSION = 1;

function App() {
  const { t, lang, setLang } = useI18n();
  const [sourceInput, setSourceInput] = useState("");
  const [normalizedHex, setNormalizedHex] = useState("");
  const [inputError, setInputError] = useState(null);
  const [isNormalizing, setIsNormalizing] = useState(false);
  const [copiedNotice, setCopiedNotice] = useState(null);
  const [parseDelimited, setParseDelimited] = useState(false);
  const [hexBuffer, setHexBuffer] = useState(null);
  const [parseTime, setParseTime] = useState(null);
  const [smartDecodeResult, setSmartDecodeResult] = useState(null);
  const [schemaText, setSchemaText] = useState("");
  const [selectedMessage, setSelectedMessage] = useState("");
  const [inputFormat, setInputFormat] = useState("");
  const [formatLocked, setFormatLocked] = useState(false);
  const [helpActiveIndex, setHelpActiveIndex] = useState(-1);
  const [decodedResult, setDecodedResult] = useState(null);
  const [hasDecoded, setHasDecoded] = useState(false);
  const [hasHydrated, setHasHydrated] = useState(false);
  const [activeDecodeSource, setActiveDecodeSource] = useState({ type: "source", label: "Source Input" });
  const [advancedActiveIndex, setAdvancedActiveIndex] = useState(-1);
  const fileInputRef = useRef(null);
  const resultRef = useRef(null);
  const shouldScrollToResultRef = useRef(false);

  const resetDecodedState = useCallback(() => {
    setHexBuffer(null);
    setDecodedResult(null);
    setParseTime(null);
    setHasDecoded(false);
  }, []);

  const onSourceChanged = e => {
    const value = e.target.value;
    setSourceInput(value);
    setInputError(null);
    setFormatLocked(false);
    setActiveDecodeSource({ type: "source", label: "Source Input" });
    resetDecodedState();
  };

  const onSubmit = () => {
    setInputError(null);

    const trimmed = sourceInput.trim();
    if (!trimmed) {
      setInputError(t("input_error_empty"));
      return;
    }

    try {
      const buffer = parseInput(sourceInput);
      setActiveDecodeSource({ type: "source", label: "Source Input" });
      shouldScrollToResultRef.current = true;
      setHexBuffer(buffer);
    } catch (error) {
      setInputError(error?.message || t("input_error_parse"));
    }
  };

  const fileChange = async e => {
    const file = (e.target.files || [])[0];
    if (file) {
      const b = new Uint8Array(await file.arrayBuffer());
      const pretty = bufferToPrettyHex(b);
      setSourceInput(pretty);
      setNormalizedHex(pretty);
      setInputFormat("file");
      setFormatLocked(true);
      setInputError(null);
      setActiveDecodeSource({ type: "source", label: "File Upload" });
      shouldScrollToResultRef.current = true;
      setHexBuffer(b);
    }
  };

  const getFormatColor = (format) => {
    switch (format) {
      case 'java-decimal-array': return 'orange';
      case 'java-escape-string': return 'purple';
      case 'hex': return 'blue';
      case 'base64': return 'green';
      case 'file': return 'teal';
      default: return 'grey';
    }
  };

  const getFormatDisplayName = (format) => {
    switch (format) {
      case 'java-decimal-array': return t('format_java_decimal_array');
      case 'java-escape-string': return t('format_java_escape_string');
      case 'hex': return t('format_hex');
      case 'base64': return t('format_base64');
      case 'file': return t('format_file');
      default: return t('format_unknown');
    }
  };

  useEffect(() => {
    const trimmed = sourceInput.trim();
    if (!trimmed) {
      setInputFormat("");
      setNormalizedHex("");
      setInputError(null);
      setIsNormalizing(false);
      if (activeDecodeSource.type === "source") {
        resetDecodedState();
      }
      return;
    }

    if (!formatLocked) {
      try {
        setInputFormat(detectInputFormat(sourceInput));
      } catch {
      }
    }

    setIsNormalizing(true);
    const t = setTimeout(() => {
      try {
        const buf = parseInput(sourceInput);
        setNormalizedHex(bufferToPrettyHex(buf));
        setInputError(null);
        setIsNormalizing(false);
      } catch (e) {
        setNormalizedHex("");
        setInputError(e?.message || "Invalid input data format");
        setIsNormalizing(false);
      }
    }, 200);

    return () => {
      clearTimeout(t);
    };
  }, [sourceInput, activeDecodeSource.type, resetDecodedState, formatLocked]);

  const canDecode = useMemo(() => {
    return Boolean(sourceInput.trim()) && !inputError;
  }, [sourceInput, inputError]);

  const copyText = useCallback(async (text, label) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopiedNotice(t("copied", { label }));
      window.setTimeout(() => setCopiedNotice(null), 1500);
    } catch (e) {
      setCopiedNotice(t("copy_failed"));
      window.setTimeout(() => setCopiedNotice(null), 1500);
    }
  }, [t]);

  useEffect(() => {
    if (!hexBuffer || !hexBuffer.length) {
      setDecodedResult(null);
      setParseTime(null);
      setHasDecoded(false);
      return;
    }

    const now = () => (
      typeof performance !== "undefined" && typeof performance.now === "function"
        ? performance.now()
        : Date.now()
    );

    const startTime = now();
    const decoded = decodeProto(hexBuffer, parseDelimited);
    const endTime = now();

    setDecodedResult(decoded);
    setParseTime(endTime - startTime);
    setHasDecoded(true);
  }, [hexBuffer, parseDelimited]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    try {
      const stored = window.localStorage.getItem(LOCAL_STORAGE_KEY);
      if (!stored) {
        setHasHydrated(true);
        return;
      }

      const parsed = JSON.parse(stored);
      if (!parsed || typeof parsed !== "object") {
        setHasHydrated(true);
        return;
      }

      if (typeof parsed.sourceInput === "string") {
        setSourceInput(parsed.sourceInput);
      } else if (typeof parsed.hex === "string") {
        setSourceInput(parsed.hex);
      }

      if (typeof parsed.inputFormat === "string") {
        setInputFormat(parsed.inputFormat);
      }

      if (typeof parsed.parseDelimited === "boolean") {
        setParseDelimited(parsed.parseDelimited);
      }

      if (typeof parsed.schemaText === "string") {
        setSchemaText(parsed.schemaText);
      }

      if (typeof parsed.selectedMessage === "string") {
        setSelectedMessage(parsed.selectedMessage);
      }

      if (
        parsed.version === STORAGE_VERSION &&
        parsed.hasDecoded &&
        (typeof parsed.sourceInput === "string" || typeof parsed.hex === "string")
      ) {
        try {
          const restoreInput = (typeof parsed.sourceInput === "string" ? parsed.sourceInput : parsed.hex) || "";
          if (!restoreInput.trim()) {
            setHasHydrated(true);
            return;
          }
          const buffer = parseInput(restoreInput);
          setHexBuffer(buffer);
        } catch (error) {
          console.warn("Failed to restore decoded buffer from cache", error);
        }
      }
    } catch (error) {
      console.warn("Failed to hydrate state from localStorage", error);
    } finally {
      setHasHydrated(true);
    }
  }, []);

  useEffect(() => {
    if (!hasHydrated || typeof window === "undefined") {
      return;
    }

    const snapshot = {
      version: STORAGE_VERSION,
      sourceInput,
      inputFormat,
      parseDelimited,
      schemaText,
      selectedMessage,
      hasDecoded,
    };

    try {
      window.localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(snapshot));
    } catch (error) {
      console.warn("Failed to persist state to localStorage", error);
    }
  }, [
    sourceInput,
    inputFormat,
    parseDelimited,
    schemaText,
    selectedMessage,
    hasDecoded,
    hasHydrated,
  ]);

  const handleSmartDecodeResult = (result) => {
    setSmartDecodeResult(result);
  };

  const schemaParser = useMemo(() => new ProtobufSchemaParser(), []);
  const schema = useMemo(() => {
    if (!schemaText.trim()) return null;
    return schemaParser.parseSchema(schemaText);
  }, [schemaText, schemaParser]);

  const validation = useMemo(() => {
    if (!schemaText.trim()) return { valid: true, errors: [] };
    return schemaParser.validateSchema(schemaText);
  }, [schemaText, schemaParser]);

  const messageOptions = useMemo(() => {
    if (!schema) return [];
    return Array.from(schema.keys()).map(name => ({
      key: name,
      text: name,
      value: name
    }));
  }, [schema]);

  useEffect(() => {
    if (!schema || !validation.valid) return;
    if (!messageOptions.length) return;
    const exists = selectedMessage && schema.has(selectedMessage);
    if (!exists) {
      setSelectedMessage(messageOptions[0].value);
    }
  }, [schema, validation.valid, messageOptions, selectedMessage]);

  const handleApplySmartBytes = (bytes, meta) => {
    if (!bytes || !bytes.length) return;
    setActiveDecodeSource({ type: "smart", label: meta?.label || "Smart Decode Output" });
    shouldScrollToResultRef.current = true;
    setHexBuffer(bytes);
  };

  const revertToSource = () => {
    setActiveDecodeSource({ type: "source", label: "Source Input" });
    resetDecodedState();
  };

  useEffect(() => {
    if (!shouldScrollToResultRef.current) return;
    if (!hexBuffer || !hexBuffer.length) return;
    const el = resultRef.current;
    if (!el) return;

    shouldScrollToResultRef.current = false;
    window.requestAnimationFrame(() => {
      if (typeof el.scrollIntoView === "function") {
        try {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        } catch {
          el.scrollIntoView();
        }
      }
    });
  }, [hexBuffer]);

  return (
    <Container>
      <Header as="h1">{t("app_title")}</Header>
      <p>
        {t("app_subtitle")}
      </p>

      <div
        className="app-action-row"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '8px',
          marginBottom: '8px',
          flexWrap: 'wrap',
        }}
      >
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <Button
            size="small"
            basic
            icon
            labelPosition="left"
            onClick={() => setHelpActiveIndex(helpActiveIndex === -1 ? 0 : -1)}
          >
            <Icon name={helpActiveIndex === -1 ? 'help circle' : 'close'} />
            {t("help")}
          </Button>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Dropdown
            selection
            compact
            value={lang}
            options={[
              { key: "en", value: "en", text: t("lang_en") },
              { key: "zh", value: "zh", text: t("lang_zh") },
            ]}
            onChange={(e, { value }) => setLang(value)}
            aria-label={t("language")}
          />
          <input
            ref={fileInputRef}
            type="file"
            style={{ display: 'none' }}
            onChange={fileChange}
          />
          <Button
            size="small"
            basic
            icon
            labelPosition="left"
            onClick={() => fileInputRef.current?.click()}
          >
            <Icon name="upload" />
            {t("upload_file")}
          </Button>
        </div>
      </div>

      {helpActiveIndex !== -1 && (
        <Accordion fluid styled style={{ marginBottom: '12px' }}>
          <Accordion.Title
            active={helpActiveIndex === 0}
            index={0}
            onClick={(e, titleProps) => {
              const idx = titleProps.index;
              setHelpActiveIndex(helpActiveIndex === idx ? -1 : idx);
            }}
          >
            <Icon name="dropdown" />
            {t("official_documentation")}
          </Accordion.Title>
          <Accordion.Content active={helpActiveIndex === 0}>
            <Message info>
              <p style={{ marginTop: 0 }}>{t("official_documentation_desc")}</p>
              <Message.List>
                <Message.Item>
                  <a href="https://protobuf.dev/programming-guides/proto3/" target="_blank" rel="noopener noreferrer">
                    {t("proto3_language_guide")}
                  </a> - {t("official_syntax_spec")}
                </Message.Item>
                <Message.Item>
                  <a href="https://protobuf.dev/programming-guides/encoding/" target="_blank" rel="noopener noreferrer">
                    {t("protocol_buffer_encoding")}
                  </a> - {t("official_encoding_spec")}
                </Message.Item>
              </Message.List>
            </Message>
          </Accordion.Content>

          <Accordion.Title
            active={helpActiveIndex === 1}
            index={1}
            onClick={(e, titleProps) => {
              const idx = titleProps.index;
              setHelpActiveIndex(helpActiveIndex === idx ? -1 : idx);
            }}
          >
            <Icon name="dropdown" />
            {t("supported_input_formats")}
          </Accordion.Title>
          <Accordion.Content active={helpActiveIndex === 1}>
            <Message info>
              <Message.List>
                <Message.Item>Hexadecimal: <code>08 96 01</code></Message.Item>
                <Message.Item>Base64: <code>CJYB</code></Message.Item>
                <Message.Item>Java Decimal Array: <code>8, 150, 1</code></Message.Item>
                <Message.Item>Java Escape String: <code>\010\226\001</code></Message.Item>
              </Message.List>
            </Message>
          </Accordion.Content>
        </Accordion>
      )}
      
      <Form>
        <Form.Group>
          <Form.Field style={{ flex: 1 }}>
            <label>{t("input_data_source")}</label>
            <TextArea
              className="app-textarea app-textarea--source"
              placeholder={t("input_placeholder")}
              onChange={onSourceChanged}
              onKeyDown={(e) => {
                if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                  e.preventDefault();
                  onSubmit();
                }
              }}
              value={sourceInput}
              data-testid="input-hex"
              rows={4}
            />
            {inputFormat && (
              <div style={{ marginTop: '8px' }}>
                <Label color={getFormatColor(inputFormat)} size="small">
                  {t("detected_format", { format: getFormatDisplayName(inputFormat) })}
                </Label>
                {isNormalizing && (
                  <Label size="small" basic style={{ marginLeft: '8px' }}>
                    {t("normalizing")}
                  </Label>
                )}
              </div>
            )}
            {inputError && (
              <Message negative style={{ marginTop: '8px' }}>
                <Message.Header>{t("input_error_title")}</Message.Header>
                <p>{inputError}</p>
              </Message>
            )}

            <Divider style={{ margin: '14px 0 10px' }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <label style={{ margin: 0 }}>{t("normalized_hex_readonly")}</label>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                {copiedNotice && (
                  <Label color="green" size="small">
                    {copiedNotice}
                  </Label>
                )}
                <Button
                  size="mini"
                  basic
                  icon
                  labelPosition="left"
                  disabled={!normalizedHex}
                  onClick={() => copyText(normalizedHex, t("normalized_hex_readonly"))}
                >
                  <Icon name="copy" />
                  {t("copy")}
                </Button>
                <Button
                  size="mini"
                  basic
                  icon
                  labelPosition="left"
                  disabled={!sourceInput}
                  onClick={() => {
                    setSourceInput("");
                    setFormatLocked(false);
                    setInputError(null);
                    setNormalizedHex("");
                    setActiveDecodeSource({ type: "source", label: "Source Input" });
                    resetDecodedState();
                  }}
                >
                  <Icon name="trash" />
                  {t("clear")}
                </Button>
              </div>
            </div>
            <TextArea
              className="app-textarea app-textarea--normalized"
              value={normalizedHex}
              readOnly
              rows={3}
              placeholder={t("normalized_hex_placeholder")}
            />
          </Form.Field>
        </Form.Group>
        <Form.Group>
          <Checkbox
            label={t("parse_delimited")}
            data-testid="parse-delimited-checkbox"
            onChange={(evt, data) => {
              setParseDelimited(data.checked);
            }}
            checked={parseDelimited}
          />
        </Form.Group>
        <Form.Button
          primary
          fluid
          onClick={onSubmit}
          data-testid="decode-button"
          disabled={!canDecode}
        >
          {t("decode")}
        </Form.Button>
      </Form>

      {hexBuffer && activeDecodeSource.type === 'smart' && (
        <Message warning style={{ marginTop: '12px' }}>
          <Message.Header>{t("using_smart_output")}</Message.Header>
          <p>{activeDecodeSource.label}</p>
          <Button size="small" onClick={revertToSource}>
            {t("revert_to_source")}
          </Button>
        </Message>
      )}
      
      {hexBuffer && (
        <div id="result" ref={resultRef} style={{ marginTop: '20px' }}>
          <Header as="h2">{t("result")}</Header>
          {parseTime !== null && (
            <Message info>
              <Message.Header>{t("performance")}</Message.Header>
              <p>{t("parse_time", { ms: parseTime.toFixed(2) })}</p>
            </Message>
          )}
          {decodedResult ? (
            <ProtobufDisplay value={decodedResult} originalData={hexBuffer} />
          ) : (
            <Message info>
              <Message.Header>{t("no_decoded_data")}</Message.Header>
              <p>{t("click_decode_hint")}</p>
            </Message>
          )}

          <Divider />
          <Header as="h3">{t("advanced")}</Header>
          <Accordion fluid styled>
            <Accordion.Title
              active={advancedActiveIndex === 0}
              index={0}
              onClick={(e, titleProps) => {
                const idx = titleProps.index;
                setAdvancedActiveIndex(advancedActiveIndex === idx ? -1 : idx);
              }}
            >
              <Icon name="dropdown" />
              {t("schema_based_display")}
            </Accordion.Title>
            <Accordion.Content active={advancedActiveIndex === 0}>
              <Form>
                <Form.Field>
                  <label>{t("protobuf_message_schema")}</label>
                  <TextArea
                    placeholder={t("schema_placeholder")}
                    value={schemaText}
                    onChange={(e) => setSchemaText(e.target.value)}
                    rows={8}
                  />
                </Form.Field>

                {validation.valid && messageOptions.length > 0 && (
                  <Form.Field>
                    <label>{t("select_message")}</label>
                    <Dropdown
                      fluid
                      search
                      clearable
                      selection
                      options={messageOptions}
                      value={selectedMessage}
                      onChange={(e, { value }) => setSelectedMessage(value)}
                      placeholder={t("select_message_placeholder")}
                      noResultsMessage={t("no_matching_message")}
                    />
                  </Form.Field>
                )}
              </Form>

              {!validation.valid && (
                <Message negative style={{ marginTop: '12px' }}>
                  <Message.Header>{t("schema_validation_error")}</Message.Header>
                  <ul>
                    {validation.errors.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </Message>
              )}

              {validation.valid && selectedMessage && decodedResult && (
                <SchemaBasedDisplay
                  key={selectedMessage}
                  decodedResult={decodedResult}
                  schemaText={schemaText}
                  selectedMessage={selectedMessage}
                />
              )}
            </Accordion.Content>

            <Accordion.Title
              active={advancedActiveIndex === 1}
              index={1}
              onClick={(e, titleProps) => {
                const idx = titleProps.index;
                setAdvancedActiveIndex(advancedActiveIndex === idx ? -1 : idx);
              }}
            >
              <Icon name="dropdown" />
              {t("smart_decode")}
            </Accordion.Title>
            <Accordion.Content active={advancedActiveIndex === 1}>
              <SmartDecoder
                originalData={hexBuffer}
                onDecodeResult={handleSmartDecodeResult}
                onApplyBytes={handleApplySmartBytes}
              />
            </Accordion.Content>
          </Accordion>
        </div>
      )}
      
      <Divider />
      <p>
      Original by pawitp. Enhanced by yibin. Contribute on{" "}
        <a href="https://github.com/yibiner/ProtoDecodeX">GitHub</a>.
      </p>
    </Container>
  );
}

export default React.memo(App);
